import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { PreplanRescueService } from "../../services/preplanrescue/preplan-rescue.service";
import { IPreplanRescue } from "../../interfaces/ert-preplan-rescue/ert-preplan-rescue";
import { LoggedUserService } from "../../services/common/logged-user.service";
import { ILoggedUserData } from "../../interfaces/common/loggedUserData";

@Component({
  selector: "app-new-ert-preplan-rescue",
  templateUrl: "./new-ert-preplan-rescue.component.html",
  styles: [
    `
      .button-container {
        display: flex;
        justify-content: space-between;
      }
      .invalid-field {
        border: 1px solid red !important;
        box-shadow: 0 0 0 0.2rem rgba(255, 0, 0, 0.25);
      }
      .mandatory {
        color: red;
      }
    `,
  ],
})
export class NewErtPreplanRescueComponent implements OnInit {
  preplanRescueForm: FormGroup;
  validationMessages: string[] = [];
  isLoading: boolean = false;
  formSubmitted: boolean = false;
  currentUser: ILoggedUserData;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private service: PreplanRescueService,
    private loggedUser: LoggedUserService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loggedUser.getLoggedUserData().subscribe((user) => {
      this.currentUser = user;
    });
  }

  /**
   * For facility id 2 the section headers are shown without the
   * "- Completed by ..." suffix.
   */
  get hideSectionSubtitles(): boolean {
    return Number(this.currentUser?.facilityId) === 2;
  }

  initForm(): void {
    this.preplanRescueForm = this.fb.group({
      // Section 1 - Completed by Maintenance
      date: ["", Validators.required],
      durationOfWork: ["", Validators.required],
      reasonForEntry: ["", Validators.required],
      maintsRep: ["", Validators.required],

      // Section 2 - Completed by Operations
      areaSTL: [""],
      hazardAssessment: [""],
      areaHO: [""],
      spaceDescription: [""],

      // Section 3 - Completed by Chevron Rescue Technician
      // Space Category
      categoryRA: [false],
      categoryRS: [false],

      spaceInfo: [""],

      // How is it accessed?
      accessHorizontal: [false],
      accessVerticalTop: [false],
      accessVerticalBottom: [false],
      accessElevated: [false],
      internalCongestion: [false],

      portalSize: ["1"],
      configuration: ["1"],

      // Medical Packaging Information
      longSpineBoard: [false],
      halfBack: [false],
      stokesBasket: [false],
      breathingAir: [false],

      // Rescue Method Haul System
      externalRetrieval: [false],
      threeOne: [false],
      internalRetrieval: [false],
      fiveOne: [false],
      rescueTechDiscretion: [false],
      rescueMethodDescription: [""],
    });
  }

  submit(): void {
    this.formSubmitted = true;
    this.validationMessages = this.validateForm();

    if (this.preplanRescueForm.valid) {
      this.isLoading = true;

      let model: IPreplanRescue = {
        id: 0,
        facility: null,
        facilityId: Number(this.currentUser?.facilityId ?? 1),
        dateTimeAdd:
          new Date(this.preplanRescueForm.get("date").value) ?? new Date(),
        createdDate: new Date(),
        createdById: this.currentUser?.id ?? 0,
        modifiedById: this.currentUser?.id ?? 0,
        modifiedDate: new Date(),
        durationOfWork: this.preplanRescueForm.get("durationOfWork").value,
        reasonOfEntry: this.preplanRescueForm.get("reasonForEntry").value,
        maintsRepOriginator: this.preplanRescueForm.get("maintsRep").value,
        areaSTL: this.preplanRescueForm.get("areaSTL").value,
        areaHO: this.preplanRescueForm.get("areaHO").value,
        hazardAssignments: this.preplanRescueForm.get("hazardAssessment").value,
        descriptionConfinedSpace:
          this.preplanRescueForm.get("spaceDescription").value,
        spaceInformation: this.preplanRescueForm.get("spaceInfo").value,
        rescueTechDescription: this.preplanRescueForm.get(
          "rescueMethodDescription"
        ).value,

        // Convert string values to numeric enum values
        portalSize: Number(this.preplanRescueForm.get("portalSize").value),
        configuration: Number(
          this.preplanRescueForm.get("configuration").value
        ),

        // Create nested objects as defined in the interface
        spaceCategory: {
          isRa: this.preplanRescueForm.get("categoryRA").value,
          isRs: this.preplanRescueForm.get("categoryRS").value,
        },

        medicalPackingInfo: {
          isLongSpineBoard: this.preplanRescueForm.get("longSpineBoard").value,
          isKED: this.preplanRescueForm.get("halfBack").value,
          isStokesBasket: this.preplanRescueForm.get("stokesBasket").value,
          isBreathingAirRequired:
            this.preplanRescueForm.get("breathingAir").value,
        },

        accessMethod: {
          isHorizontal: this.preplanRescueForm.get("accessHorizontal").value,
          isVerticalTop: this.preplanRescueForm.get("accessVerticalTop").value,
          isVerticalBottom: this.preplanRescueForm.get("accessVerticalBottom")
            .value,
          isAccessElevated: this.preplanRescueForm.get("accessElevated").value,
        },

        rescueMethodHaulSystem: {
          isExternalRetrieval:
            this.preplanRescueForm.get("externalRetrieval").value,
          isInternalRetrieval:
            this.preplanRescueForm.get("internalRetrieval").value,
          is31: this.preplanRescueForm.get("threeOne").value,
          is51: this.preplanRescueForm.get("fiveOne").value,
          isRescueTechDiscretion: this.preplanRescueForm.get(
            "rescueTechDiscretion"
          ).value,
        },
      };

      this.service.addPreplanRescue(model).subscribe((res) => {
        this.isLoading = false;
        this.router.navigate(["/preplan-rescue/edit", res.id]);
      });

      setTimeout(() => {
        this.isLoading = false;
      }, 1000);
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.preplanRescueForm.get(fieldName);
    return this.formSubmitted && field && field.invalid;
  }

  validateForm(): string[] {
    return this.service.validatePreplanRescueForm(this.preplanRescueForm);
  }

  cancel(): void {
    this.router.navigate(["/preplan-rescue"]);
  }
}
